import { useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { Box } from '@mui/material'
import { useState } from 'react'

import { FeatureMessage } from '@/components/common/UpgradeMessage'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_PRODUCT_DASHBOARD_STATS,
  GET_TENANT_ARRAY_SETTINGS,
  getTenantArraySettingsQuery,
  getTenantArraySettingsQueryVariables,
  ProductsDashboardStatsQuery,
  ProductsDashboardStatsQueryVariables,
} from '@/graphql'
import { usePosDataSetupStatus } from '@/hooks/use-pos-data-setup-status'
import useT from '@/i18n/useT'
import PieChart from '@/pages/components/PieChart'
import SalesCategorySettings from '@/pages/revenue/components/SalesCategorySettings'

type Props = {
  startDate?: string | null
  endDate?: string | null
  storeIds?: string[]
}

export const ProductCategoryRevPieChart: React.FC<Props> = props => {
  const { startDate, endDate, storeIds } = props
  const [updateSettings, setUpdateSettings] = useState<boolean>(false)
  const { t } = useT()
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()

  const {
    data,
    loading: loadingCategories,
    refetch: refetchCategories,
  } = useQuery<
    getTenantArraySettingsQuery,
    getTenantArraySettingsQueryVariables
  >(GET_TENANT_ARRAY_SETTINGS)
  const insideSalesCategories =
    data?.tenantArraySettings?.insideSalesCategories || []

  const {
    data: productDashboardStats,
    loading: productDashboardStatsLoading,
    refetch: refetchProductDashboardStats,
  } = useQuery<
    ProductsDashboardStatsQuery,
    ProductsDashboardStatsQueryVariables
  >(GET_PRODUCT_DASHBOARD_STATS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filters: {
        startDate,
        endDate,
        storeIds,
        groupBy: 'category',
        categories: insideSalesCategories,
      },
      pagination: {
        limit: 3000,
        offset: 0,
      },
    },
  })

  const handleRefresh = () => {
    refetchProductDashboardStats()
    refetchCategories()
  }
  useRefresh(handleRefresh)

  const { hasDataInBigQuery, noLinkedStores, numUnlinkedStores, loading } =
    usePosDataSetupStatus({ storeIds })

  return (
    <>
      <PieChart
        noDataMessage={t('categoriesTable.noData')}
        title={t('homeDashboard.insideRevenueByCategory')}
        loading={productDashboardStatsLoading || loadingCategories || loading}
        data={
          productDashboardStats?.productsDashboardStats?.results?.map(item => ({
            id: item.name,
            value: item.totalRevenue,
          })) ?? []
        }
        onSettingsPress={() => setUpdateSettings(true)}
        onLabelClick={categoryId => {
          handleNavTo(`/products`, {
            replaceParams: {
              categories: [categoryId || ''],
              storeIds: storeIds || [],
            },
          })
        }}
        BodyComponent={
          (!hasDataInBigQuery || noLinkedStores) && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {!hasDataInBigQuery ? (
                <FeatureMessage
                  sx={{ width: '100%', maxWidth: 400 }}
                  message={t('noSalesData.message')}
                  buttonText={t('noSalesData.button')}
                  onClick={() => {
                    handleNavTo('/admin/sales-data')
                  }}
                />
              ) : (
                <FeatureMessage
                  sx={{ width: '100%', maxWidth: 400 }}
                  message={t('unlinkedStores.message', {
                    numUnlinkedStores,
                  })}
                  buttonText={t('unlinkedStores.button')}
                  onClick={() => {
                    handleNavTo('/admin/sales-data')
                  }}
                />
              )}
            </Box>
          )
        }
      />
      <SalesCategorySettings
        setting={'inside_sales_categories'}
        open={updateSettings}
        onClose={() => setUpdateSettings(false)}
      />
    </>
  )
}
